import { ORDER_STATUS_CREATED } from "../constants/OrderConstant";
import { PICK_DAY_GAP_DAYS, PICK_DAY_STATUS, PICKING_STATUS } from "../constants/PickingConstant";

/**
 * Pick day versus delivery date.
 *
 * They are not the same day. Saturday deliveries are packed on Friday, which is why the orders
 * page has always pulled Saturday in when a Friday is selected. And the client wants a day in
 * between picking and delivery ("vandaag besteld, morgen gepikt, overmorgen bezorgd"), which is a
 * planning change for them but must already be possible here.
 *
 * Ported from api PickDayCalculator (branch picqer-koppeling) and the CMS `pickDayForDate`, and
 * extended with that gap. There is now one implementation of the Friday/Saturday rule instead of
 * three; the orders page, the pick day lock and the waves all ask this file.
 *
 * Dates are the DD-MM-YYYY strings used in Firestore (`delivery.date`). They are parsed by hand
 * rather than with dayjs: this app does not load dayjs' customParseFormat plugin, so
 * `dayjs("08-08-2026", "DD-MM-YYYY")` silently ignores the format (see functions/api/trustpilot).
 * All arithmetic is in UTC, so a daylight saving switch can never shift a date.
 *
 * Pure: no Firestore, no React.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const SATURDAY = 6;
const SUNDAY = 0;
const FRIDAY = 5;

export const parseDate = (value) => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value || "");
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1])));
  // Reject 31-02-2026 and friends instead of letting Date roll them into March
  return date.getUTCDate() === Number(match[1]) && date.getUTCMonth() === Number(match[2]) - 1 ? date : null;
};

const pad = (number) => String(number).padStart(2, "0");

export const formatDate = (date) =>
  `${pad(date.getUTCDate())}-${pad(date.getUTCMonth() + 1)}-${date.getUTCFullYear()}`;

export const addDays = (value, days) => {
  const date = typeof value === "string" ? parseDate(value) : value;
  if (!date) return null;
  return formatDate(new Date(date.getTime() + days * DAY_MS));
};

/** 0 = Sunday ... 6 = Saturday, like Date#getDay. */
export const dayOfWeek = (value) => {
  const date = parseDate(value);
  return date === null ? null : date.getUTCDay();
};

export const isFriday = (value) => dayOfWeek(value) === FRIDAY;

/**
 * The pick day an order with this delivery date is picked on.
 *
 * Nobody picks on a weekend, so a pick day that would land on Saturday moves back to Friday. With
 * no day in between (gap 0) Sunday is left alone: nothing is delivered on Sunday, and rolling it
 * would add Sunday to Friday's query for no reason - at gap 0 this reproduces exactly what the
 * orders page has always done. With a gap, a Monday delivery would be picked on Sunday, so Sunday
 * rolls back to Friday too.
 */
export const pickDayForDeliveryDate = (deliveryDate, { gapDays = PICK_DAY_GAP_DAYS } = {}) => {
  const date = parseDate(deliveryDate);
  if (!date) return null;
  let base = new Date(date.getTime() - gapDays * DAY_MS);
  const day = base.getUTCDay();
  if (day === SATURDAY) base = new Date(base.getTime() - DAY_MS);
  else if (day === SUNDAY && gapDays > 0) base = new Date(base.getTime() - 2 * DAY_MS);
  return formatDate(base);
};

/**
 * The reverse: every delivery date that is picked on this pick day, in date order. Empty when
 * the day is not a pick day at all (a Saturday).
 */
export const deliveryDatesForPickDay = (pickDay, { gapDays = PICK_DAY_GAP_DAYS } = {}) => {
  const date = parseDate(pickDay);
  if (!date) return [];
  const dates = [];
  // A delivery date is at least `gap` days after its pick day, and the weekend roll moves a pick
  // day back by at most two days - so three candidates cover every case.
  for (let offset = gapDays; offset <= gapDays + 2; offset += 1) {
    const candidate = formatDate(new Date(date.getTime() + offset * DAY_MS));
    if (pickDayForDeliveryDate(candidate, { gapDays }) === pickDay) dates.push(candidate);
  }
  return dates;
};

/**
 * What the orders page fetches for a selected delivery date. Always at gap 0: the page is a view
 * of delivery dates, and "Friday also shows Saturday" is there because Saturday is packed on
 * Friday - not because of any day in between. Selecting a Saturday still shows only Saturday.
 */
export const deliveryDatesForOrdersPage = (selectedDate) =>
  pickDayForDeliveryDate(selectedDate, { gapDays: 0 }) === selectedDate
    ? deliveryDatesForPickDay(selectedDate, { gapDays: 0 })
    : [selectedDate];

/** Every day from `from` up to and including `to`; empty when either is not a date or `to` comes first. */
export const daysBetween = (from, to) => {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end || end.getTime() < start.getTime()) return [];
  const days = [];
  for (let time = start.getTime(); time <= end.getTime(); time += DAY_MS) days.push(formatDate(new Date(time)));
  return days;
};

/** All days from `today` up to and including `horizonDays` ahead. */
export const pickDaysWithin = (today, horizonDays) => {
  if (!parseDate(today) || horizonDays < 0) return [];
  return Array.from({ length: horizonDays + 1 }, (_, index) => addDays(today, index));
};

// --- pick day state --------------------------------------------------------------------------

/** No document means the day was never released: open. */
export const isPickDayLocked = (pickDayDoc) => !!pickDayDoc && pickDayDoc.status === PICK_DAY_STATUS.RELEASED;

/**
 * The document after releasing. Releasing always locks - there is no external system any more
 * that could refuse part of the day, which is why Picqer only locked on success.
 */
export const releasePickDay = (existing, { pickDay, uid, email, now, orderCount, deliveryDates, gapDays }) => ({
  ...(existing || {}),
  date: pickDay,
  status: PICK_DAY_STATUS.RELEASED,
  releasedAt: now,
  releasedBy: { uid: uid || null, email: email || null },
  unlockedAt: null,
  unlockedBy: null,
  orderCount,
  deliveryDates,
  gapDays,
  updatedAt: now,
});

/** Back to open. releasedAt/releasedBy stay: they are the history of who released it last. */
export const unlockPickDay = (existing, { uid, email, now }) => ({
  ...(existing || {}),
  status: PICK_DAY_STATUS.OPEN,
  unlockedAt: now,
  unlockedBy: { uid: uid || null, email: email || null },
  updatedAt: now,
});

// --- which orders belong to a pick day ---------------------------------------------------------

/**
 * The orders a pick day is about: on one of its delivery dates, past checkout (not CREATED), and
 * not gift cards only. The same rule the orders page query applies, plus the gift card filter
 * every printed document applies.
 */
export const ordersForPickDay = (orders, pickDay, { gapDays = PICK_DAY_GAP_DAYS } = {}) => {
  const dates = new Set(deliveryDatesForPickDay(pickDay, { gapDays }));
  return (orders || []).filter(
    (order) =>
      !!order &&
      dates.has(order.delivery?.date) &&
      !!order.orderStatus &&
      order.orderStatus !== ORDER_STATUS_CREATED &&
      !order.containsOnlyGiftCards,
  );
};

/** Counts for the day panel, keyed on `order.picking.status`. */
export const summarisePickDay = (orders) => {
  const summary = { total: 0, unassigned: 0, queued: 0, picking: 0, picked: 0, partial: 0, aborted: 0 };
  (orders || []).forEach((order) => {
    if (!order || order.containsOnlyGiftCards) return;
    summary.total += 1;
    const status = order.picking?.waveId ? order.picking.status : null;
    if (!status) summary.unassigned += 1;
    else if (status === PICKING_STATUS.QUEUED) summary.queued += 1;
    else if (status === PICKING_STATUS.PICKING) summary.picking += 1;
    else if (status === PICKING_STATUS.PICKED) summary.picked += 1;
    else if (status === PICKING_STATUS.PARTIAL) summary.partial += 1;
    else if (status === PICKING_STATUS.ABORTED) summary.aborted += 1;
  });
  return summary;
};
